//#region imports

// react
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { app, firestore } from "_utils/firebase-app";
import { collection, onSnapshot, getDocs } from "firebase/firestore";

// redux
import { useSelector, useDispatch } from 'react-redux';
import { selectFirebaseInfo, selectUserDb } from "_redux/slices/main-screen/user";
import { getAirportUuidAndCoordinates, SelectCurrentCoordinates } from "_redux/slices/timeline/timeline-live-location-tab";
import { SelectTenant } from "_redux/slices/timeline/timeline-live-location-tab";
import { resolveWifiLocation, selectWifiLocations } from "_redux/slices/asset-tracking/asset-tracking";
import { getAirportLocation } from "_redux/slices/timeline/timeline-areas";

// custom components
import Sidebar from './sidebar';
import AssetTrackingLiveView from './asset-tracking-live-view';
import AssetTrackingMap from './asset-tracking-map';
import AssetTrackingLocation from "./asset-tracking-locations";
import AssetTrackingCoverage from "./asset-tracking-coverage";
import AssetTrackingInspect from "./asset-tracking-inspect";
import AssetTrackingTrail from "./asset-tracking-trail";
import AssetTrackingIdleReport from "./asset-tracking-idle-report";
import AssetTrackingMovementHeatmap from "./asset-tracking-movement-heatmap";
import AssetTrackingInfrastructure from "./asset-tracking-infrastructure";

// styling
import { Colors, Spacing } from "_styles";
import './styles.css';

//#endregion


const AssetTrackingTab = (props) => {

    //#region local states
    const [applications, setApplications] = useState({});
    const [activeApp, setActiveApp] = useState(null);
    const [devices, setDevices] = useState([]);
    const [activeTab, setActiveTab] = useState('overview');
    const [trailDevice, setTrailDevice] = useState(null);
    const tenant = useSelector(SelectTenant);
    const [assetStateFilter, setAssetStateFilter] = useState('Idle');
    const [now, setNow] = useState(Date.now());
    const unsubscribeRefs = useRef([]);

    // Gateway state — lifted here so both Sidebar and Map share one poll
    const [gateways, setGateways] = useState([]);
    const [gwLoading, setGwLoading] = useState(false);
    const [gwError, setGwError] = useState(null);

    const [feedError, setFeedError] = useState(false);
    const [hiddenEuis, setHiddenEuis] = useState(new Set());
    const toggleHidden = (eui) => setHiddenEuis(prev => {
        const next = new Set(prev);
        next.has(eui) ? next.delete(eui) : next.add(eui);
        return next;
    });
    //#endregion

    //#region redux
    const dispatch = useDispatch();
    const dbUser = useSelector(selectFirebaseInfo);
    const userDb = useSelector(selectUserDb);
    const wifiLocations = useSelector(selectWifiLocations);
    const isAdmin = userDb?.role === 'ADMIN';
    //#endregion

    //#region handlers

    // Fetch & listen to applications
    async function listenTenantApplications(tenantId) {
        // Cancel any existing listeners before creating new ones
        unsubscribeRefs.current.forEach(fn => fn());
        unsubscribeRefs.current = [];

        const appsRef = collection(firestore, "tenants", tenantId, "applications");
        const appsSnap = await getDocs(appsRef);
        appsSnap.forEach((appDoc) => {
            const appName = appDoc.id;
            const devicesRef = collection(
                firestore,
                "tenants",
                tenantId,
                "applications",
                appName,
                "devices"
            );

            const unsub = onSnapshot(
                devicesRef,
                (snapshot) => {
                    setFeedError(false);
                    const devices = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    setApplications(prev => ({ ...prev, [appName]: devices }));
                },
                (err) => {
                    console.error('Firestore device feed error:', err);
                    setFeedError(true);
                }
            );

            unsubscribeRefs.current.push(unsub);
        });
    }
    //#endregion

    //#region memos

    // filtered devices based on asset state
    const filteredDevices = useMemo(() => {
        const FIVE_MINUTES = 5 * 60; // in seconds

        return devices.filter((device) => {
            const lastMotionTs = device?.meta?.deviceLastMotionAt;
            const latestAt     = device?.latest?.at;

            const lastMotionSec = lastMotionTs ? (now - lastMotionTs.toDate()) / 1000 : Infinity;
            const latestAtSec   = latestAt     ? (now - latestAt.toDate())     / 1000 : Infinity;

            // Active = fPort 8 start-of-movement within 5 min (covers trip start before first fix)
            //       OR any uplink within 5 min (in-trip fixes keep this fresh; heartbeat is 12h so no false positives)
            const isActive = lastMotionSec <= FIVE_MINUTES || latestAtSec <= FIVE_MINUTES;

            if (assetStateFilter === "Active") return isActive;
            if (assetStateFilter === "Idle")   return !isActive;
            return true;
        });

    }, [devices, assetStateFilter, applications, now]);

    //#endregion

    //#region useEffects

    // on load - get airport uuid and coordinates
    useEffect(() => {
        const iata = dbUser?.details?.authority;
        if (!iata) return;

        dispatch(getAirportUuidAndCoordinates(iata));
        dispatch(getAirportLocation(iata));
    }, [dbUser]);

    // Initial load - listen to applications and fetch beacons
    useEffect(() => {
        if (tenant) {
            listenTenantApplications(tenant);
        }
        return () => {
            unsubscribeRefs.current.forEach(fn => fn());
            unsubscribeRefs.current = [];
        };
    }, [tenant]);

    // once applications are loaded, set the first application as active, and load its devices
    useEffect(() => {
        const appNames = Object.keys(applications);

        if (appNames.length > 0 && !activeApp) {
            const firstApp = appNames[0];
            setDevices(applications[firstApp] || []);
            setActiveApp(firstApp);
        }
        else if (activeApp && applications[activeApp]) {
            setDevices(applications[activeApp]);
        }
    }, [applications]);

    // Update devices when application changes
    useEffect(() => {
        if (activeApp && applications[activeApp]) {
            setDevices(applications[activeApp]);
        }
    }, [activeApp]);

    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 10000);
        return () => clearInterval(id);
    }, []);

    // Resolve wifi positions for any device reporting positioningType === 'wifi'.
    // Cache by resolvedAt (device.latest.at millis) — skips re-dispatch for the same fix.
    useEffect(() => {
        const iata = dbUser?.details?.authority;
        if (!iata || !devices.length) return;
        for (const device of devices) {
            if (device?.latest?.positioningType !== 'wifi') continue;
            const scans = device?.latest?.wifiItems;
            if (!scans?.length) continue;
            const resolvedAt = device?.latest?.at?.toMillis?.() ?? 0;
            if (wifiLocations[device.id]?.resolvedAt === resolvedAt) continue;
            dispatch(resolveWifiLocation({ devEui: device.id, iata, scans, resolvedAt }));
        }
    }, [devices]);

    // Gateway poll — single source of truth for sidebar + map
    const pollGateways = useCallback(async () => {
        if (!tenant) return;
        setGwLoading(true);
        setGwError(null);
        try {
            const res = await fetch(`/chirpstack/api/gateways?limit=100&tenantId=${tenant}`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setGateways(data.result ?? []);
        } catch (e) {
            console.error('Gateway poll failed:', e);
            setGwError('Failed to load gateways');
        } finally {
            setGwLoading(false);
        }
    }, [tenant]);

    useEffect(() => {
        pollGateways();
        const id = setInterval(pollGateways, 2 * 60 * 1000);
        return () => clearInterval(id);
    }, [pollGateways]);
    //#endregion


    //#region 404Component
    const NoAccess = () => (
        <div style={{
            height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#f0f6ff', borderRadius: 10,
        }}>
            <div
                style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
                    maxWidth: 360, textAlign: 'center', padding: '32px 24px',
                    background: '#fff', borderRadius: 16,
                    border: '1px solid #dbeafe',
                    boxShadow: '0 4px 20px rgba(48,127,226,0.1)',
                }}>
                <div style={{
                    width: 64, height: 64, borderRadius: '50%',
                    background: '#E6F0FB',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="#307FE2" strokeWidth="1.8">
                        <path strokeLinecap="round" strokeLinejoin="round"
                            d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    </svg>
                </div>
                <div>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e3a5f', marginBottom: 4 }}>
                        Admin access required
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#64748b', lineHeight: 1.5 }}>
                        You don't have permission to view this tab. Contact your administrator if you think this is a mistake.
                    </div>
                </div>
            </div>
        </div>
    );

    //#endregion

    return (
        <div className="d-flex gap-2" style={{ height: '100vh', width: '100%', padding: Spacing.SCALE_4, background: '#f7f7f7' }} id='assetTrackingDiv'>

            {/* sidebar — slides out on tabs that have their own control panel */}
            <div style={{
                maxWidth: ['trail', 'idleReport', 'heatmap', 'infrastructure'].includes(activeTab) ? 0 : 320,
                opacity: ['trail', 'idleReport', 'heatmap', 'infrastructure'].includes(activeTab) ? 0 : 1,
                overflow: 'hidden',
                flexShrink: 0,
                transition: 'max-width 0.25s ease, opacity 0.2s ease',
            }}>
                <Sidebar
                    applications={applications}
                    activeApp={activeApp}
                    setActiveApp={setActiveApp}
                    devices={devices}
                    assetStateFilter={assetStateFilter}
                    setAssetStateFilter={setAssetStateFilter}
                    onSeeTrail={(device) => { setTrailDevice(device); setActiveTab('trail'); }}
                    tenant={tenant}
                    hiddenEuis={hiddenEuis}
                    onToggleHidden={toggleHidden}
                    gateways={gateways}
                    gwLoading={gwLoading}
                    gwError={gwError}
                    pollGateways={pollGateways}
                />
            </div>

            {/* main content - tabs */}
            <div
                className='border'
                style={{
                    flex: 1,
                    height: '100%',
                    background: '#fff',
                    borderRadius: 12,
                    padding: '14px 18px 18px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                    width: '100%'
                }}
            >
                {/* Custom tab bar */}
                <div className='' style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    background: '#f0f6ff',
                    borderRadius: 10,
                    padding: 4,
                    marginBottom: 14,
                    flexShrink: 0,
                    width: 'fit-content',
                    alignSelf: 'center'
                }}>
                    {[
                        { key: 'overview', label: 'Live View' },

                        // TODO: Hide locations & maps tab until review
                        // { key: 'locationsTable', label: 'Locations' },
                        // { key: 'map',            label: 'Map'       },
                        { key: 'trail', label: 'Trail' },
                        { key: 'idleReport', label: 'Idle Report' },
                        { key: 'heatmap', label: 'Heatmap' },
                        { key: 'infrastructure', label: 'Infrastructure' },
                        ...(isAdmin ? [
                            { key: 'coverage', label: 'Coverage' },
                            { key: 'inspect', label: 'Inspect' },
                        ] : []),
                    ].map(({ key, label }) => {
                        const active = activeTab === key;
                        return (
                            <button
                                key={key}
                                onClick={() => setActiveTab(key)}
                                style={{
                                    padding: '6px 18px',
                                    borderRadius: 7,
                                    border: 'none',
                                    fontSize: '0.83rem',
                                    fontWeight: active ? 700 : 500,
                                    cursor: 'pointer',
                                    background: active ? '#307FE2' : 'transparent',
                                    color: active ? '#fff' : '#64748b',
                                    boxShadow: active ? '0 2px 8px rgba(48,127,226,0.3)' : 'none',
                                    transition: 'all 0.15s ease',
                                    whiteSpace: 'nowrap',
                                }}
                            >{label}</button>
                        );
                    })}
                </div>

                {/* Feed error banner */}
                {feedError && (
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
                        padding: '8px 14px', margin: '4px 0', fontSize: '0.8rem', color: '#991b1b',
                    }}>
                        <span>⚠️ Live feed disconnected. Device positions may be stale. Reconnecting…</span>
                        <button
                            onClick={() => setFeedError(false)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b', fontSize: '1rem', lineHeight: 1, padding: '0 4px' }}
                        >×</button>
                    </div>
                )}

                {/* Tab panels */}
                <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    {activeTab === 'overview' && (
                        <div style={{ height: '100%', width: '100%' }}>
                            <AssetTrackingLiveView devices={filteredDevices.filter(d => !hiddenEuis.has(d.id))} gateways={gateways} wifiLocations={wifiLocations} />
                        </div>
                    )}

                    {/* TODO: Hide locations & maps tab until review */}
                    {/* {activeTab === 'locationsTable' && (
                        <div style={{ height: '100%', width: '100%' }}>
                            <AssetTrackingLocation devices={filteredDevices} wifiLocations={wifiLocations} />
                        </div>
                    )}
                    {activeTab === 'map' && (
                        <div style={{ height: '100%', width: '100%' }}>
                            <AssetTrackingMap devices={filteredDevices} gateways={gateways} wifiLocations={wifiLocations} />
                        </div>
                    )} */}
                    {activeTab === 'trail' && (
                        <div style={{ height: '100%', width: '100%' }}>
                            <AssetTrackingTrail devices={devices} initialDevice={trailDevice} />
                        </div>
                    )}
                    {activeTab === 'idleReport' && (
                        <div style={{ height: '100%', width: '100%' }}>
                            <AssetTrackingIdleReport devices={devices} />
                        </div>
                    )}
                    {activeTab === 'heatmap' && (
                        <div style={{ height: '100%', width: '100%' }}>
                            <AssetTrackingMovementHeatmap devices={devices} />
                        </div>
                    )}
                    {activeTab === 'infrastructure' && (
                        <div style={{ height: '100%', width: '100%' }}>
                            <AssetTrackingInfrastructure />
                        </div>
                    )}
                    {activeTab === 'coverage' && (
                        isAdmin
                            ? <AssetTrackingCoverage devices={devices} gateways={gateways} />
                            : <NoAccess />
                    )}
                    {activeTab === 'inspect' && (
                        isAdmin
                            ? <AssetTrackingInspect devices={devices} />
                            : <NoAccess />
                    )}
                </div>
            </div>

        </div>
    );
};

export default AssetTrackingTab;
